import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { map, take } from 'rxjs';
import { Color } from '../../models/new/style';
import { Shot } from '../../models/new/shot';
import { MediaShareDetail, MediaShareMenuBrand } from '../../models/new/media-share-detail';
import { MediaShareDetailMapper } from '../../services/media-share-detail-mapper';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { ImageGalleryComponent } from 'src/app/shared/components/image-gallery/image-gallery.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { SharedQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { CreateMediaShareLinkModalComponent } from '../company-media-share-link-list/create-media-share-link-modal/create-media-share-link-modal.component';
import { AddDetailToMediaShareLinkInput } from '../../models/types/add-detail-to-media-share-link-input.type';
import { environment } from 'src/environments/environment';

@Component({
  standalone: false,
  selector: 'bt-company-media-share-link-detail',
  templateUrl: './company-media-share-link-detail.component.html',
  styleUrls: ['./company-media-share-link-detail.component.scss']
})
export class CompanyMediaShareLinkDetailComponent implements OnInit {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #graphqlService = inject(GraphQLService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialog = inject(MatDialog);
  readonly #mediaShareDetailMapper = inject(MediaShareDetailMapper);
  readonly #utilitiesService = inject(UtilitiesService);

  mediaShareLinkId = '';
  loading = false;
  mediaShareDetails: Array<MediaShareDetail> = [];
  menu: Array<MediaShareMenuBrand> = [];
  activeModelId: number | null = null;

  ngOnInit(): void {
    this.mediaShareLinkId = this.#route.snapshot.paramMap.get('mediaShareLinkId') ?? '';
    this.getMedia();
  }

  onBack(): void {
    this.#router.navigate(['/company/media-share-link-list']);
  }

  getShareLink(): string {
    const baseUrl = (environment.frontendUrl || '').replace(/\/$/, '');
    return `${baseUrl}/share-media/${this.mediaShareLinkId}`;
  }

  copyShareLink(): void {
    navigator.clipboard.writeText(this.getShareLink()).then(
      () => {
        this.#utilitiesService.showSnackbar('Link copied to clipboard', SnackbarType.Success);
      },
      () => {
        this.#utilitiesService.showSnackbar('Error copying link', SnackbarType.Error);
      }
    );
  }

  getMedia() {
    this.loading = true;
    this.#graphqlService
      .getData(SharedQueries.GET_MEDIA_SHARE_DETAILS, { mediaShareLinkId: this.mediaShareLinkId })
      .pipe(
        take(1),
        takeUntilDestroyed(this.#destroyRef),
        map((res: any) => res['data']['mediaShareLinkDetails'])
      )
      .subscribe({
        next: (media) => {
          const details = this.#mediaShareDetailMapper.toMediaShareDetails(media?.styles);
          this.menu = this.#mediaShareDetailMapper.toMediaShareMenu(details);
          // stesso ordine del menu, cosi' la lista a destra segue l'albero a sinistra
          this.mediaShareDetails = [];
          this.menu.forEach((brand) =>
            brand.collections.forEach((collection) =>
              collection.styleGroups.forEach((group) => this.mediaShareDetails.push(...group.products))
            )
          );
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  addProduct() {
    this.#dialog
      .open(CreateMediaShareLinkModalComponent)
      .afterClosed()
      .subscribe((result) => {
        if (result?.modelColors?.length) {
          this.addProducts(result.modelColors);
        }
      });
  }

  getProductAnchorId(product: MediaShareDetail): string {
    return `media-share-product-${product.style.modelId}`;
  }

  scrollToProduct(product: MediaShareDetail): void {
    this.activeModelId = product.style.modelId ?? null;
    product.opened = true;
    setTimeout(() =>
      document.getElementById(this.getProductAnchorId(product))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    );
  }

  toggle(product: MediaShareDetail) {
    product.opened = !product.opened;
  }

  viewGallery(gallery: { shots: Shot[]; first?: number }): void {
    if (!gallery.shots.length) {
      return;
    }

    this.#dialog.open(ImageGalleryComponent, {
      data: gallery
    });
  }

  removeProduct(product: MediaShareDetail) {
    const mediaShareLinkDetailIds = (product.colors || []).map((colorShot) => colorShot.mediaShareLinkDetailId);

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove Product',
          message: `Are you sure you want to remove "${product.style?.name} - ${product.style?.code}"?`
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.removeDetails(mediaShareLinkDetailIds, 'Product removed successfully', 'Error during removing product');
        }
      });
  }

  addProducts(modelColors: Array<{ modelId: number; colorSizes: Array<{ colorId: number }> }>) {
    if (!modelColors?.length) {
      return;
    }

    const addDetailToMediaShareLinkInput: AddDetailToMediaShareLinkInput = {
      mediaShareLinkId: this.mediaShareLinkId,
      modelColors
    };

    this.loading = true;
    this.#graphqlService
      .postData(MutationQuery.ADD_DETAIL_TO_MEDIA_SHARE_LINK, {
        addDetailToMediaShareLinkInput
      })
      .subscribe({
        next: (res: any) => {
          const payload = res?.data?.addDetailToMediaShareLink;
          if (payload?.success === false) {
            this.loading = false;
            this.#utilitiesService.showSnackbar(payload.message || 'Error during adding products', SnackbarType.Error);
            return;
          }

          this.#utilitiesService.showSnackbar(payload?.message || 'Products added successfully', SnackbarType.Success);
          this.getMedia();
        },
        error: (err) => {
          this.loading = false;
          this.#utilitiesService.showSnackbar(
            err?.networkError?.error?.errors?.[0]?.message || 'Error during adding products',
            SnackbarType.Error
          );
        }
      });
  }

  removeColor(product: MediaShareDetail, color: Color) {
    if (color.id == null) {
      return;
    }
    const mediaShareLinkDetailIds = product.colors
      .filter((colorShot) => colorShot.color.id === color.id)
      .map((colorShot) => colorShot.mediaShareLinkDetailId);

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove Color',
          message: `Are you sure you want to remove "${color.name}" color from "${product.style?.name} - ${product.style?.code}"?`
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.removeDetails(mediaShareLinkDetailIds, 'Color removed successfully', 'Error during removing color');
        }
      });
  }

  private removeDetails(mediaShareLinkDetailIds: Array<number>, successMessage: string, errorMessage: string) {
    if (!mediaShareLinkDetailIds.length) {
      return;
    }

    this.loading = true;

    this.#graphqlService
      .postData(MutationQuery.REMOVE_DETAIL_FROM_MEDIA_SHARE_LINK, {
        removeDetailFromMediaShareLinkInput: { mediaShareLinkDetailIds }
      })
      .subscribe({
        next: () => {
          this.#utilitiesService.showSnackbar(successMessage, SnackbarType.Success);
          this.getMedia();
        },
        error: (err) => {
          this.loading = false;
          this.#utilitiesService.showSnackbar(
            err?.networkError?.error?.errors?.[0]?.message || errorMessage,
            SnackbarType.Error
          );
        }
      });
  }
}
